import { Loader2, Save, SquarePen, X } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import { Textarea } from "@ui/textarea"
import { useUserSession } from "@session/UserSession"

import { CollectionItem } from "@vertesia/common"

interface UpdateReportDescriptionPanelProps {
    program: CollectionItem
    configuration: CollectionItem | undefined
    onUpdate: (value: string) => void
}
export function UpdateReportDescriptionPanel({ program, configuration, onUpdate }: Readonly<UpdateReportDescriptionPanelProps>) {
    const { store } = useUserSession()

    const [isUpdating, setIsUpdating] = useState<boolean>(false)
    const [descriptionUpdate, setDescriptionUpdate] = useState<boolean>(false)
    const [updatedDescription, setUpdatedDescription] = useState<string | undefined>("")
    
    const handleOpenTitleUpdate = () => {
        setUpdatedDescription(program.description)
        setDescriptionUpdate(true)
    }

    const handleSaveTitleUpdate = () => {
        if (!store || !updatedDescription) {
            return
        }

        setIsUpdating(true)
        store.collections.update(program.id, { description: updatedDescription })
            .then(() => {
                onUpdate(updatedDescription)
                handleCloseTitleUpdate()
            })
            .catch((error) => {
                toast.error("Program update error", {
                    description: error.message || "An error occurred while updating the program.",
                    duration: 3000
                })
            })
            .finally(() => {
                setIsUpdating(false)
            })
    }

    const handleCloseTitleUpdate = () => {
        setUpdatedDescription(undefined)
        setDescriptionUpdate(false)
    }

    return (
        <>
            <div className="flex justify-between">
                <div className="flex items-center gap-1">
                    <span className="font-semibold tracking-tight text-xl">Report Details</span>
                    {
                        descriptionUpdate ? (
                            <>
                                <Button variant={"ghost"} size={"xs"} onClick={handleSaveTitleUpdate} disabled={isUpdating || !updatedDescription?.length}>
                                    { isUpdating ? <Loader2 className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" /> : <Save /> }
                                </Button>
                                {
                                    !isUpdating && (
                                        <Button variant={"ghost"} size={"xs"} onClick={handleCloseTitleUpdate} disabled={isUpdating}>
                                            <X />
                                        </Button>
                                    )
                                }
                            </>
                        ) : (
                            <Button variant={"ghost"} size={"xs"} onClick={handleOpenTitleUpdate}>
                                <SquarePen />
                            </Button>
                        )
                    }
                </div>
                {
                    configuration &&
                    <Badge className="flex text-sm capitalize">
                        {configuration.name}
                    </Badge>
                }
            </div>
            {
                descriptionUpdate ? (
                    <Textarea value={updatedDescription} onChange={(e) => setUpdatedDescription(e.target.value)} placeholder={"Program Description"} disabled={isUpdating} />
                ) : (
                    <p className="text-muted-foreground">{program.description}</p>
                )
            }
        </>
    )
}